import PropTypes from 'prop-types';
import { useState } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useModal } from '../../../../context/ModalContext';
import SimpleTrans from '../../../Trans/SimpleTrans';
import ContactList from '../../../ContactList';
import RightSidebar from '../../../RightSidebar/RightSidebar';
import RoomAvatar from '../../../RoomAvatar';
import './Details.css';

const DetailsHeader = ({
  id = null,
  title = '',
  topic = '',
  avatars = [],
  onEditClick = () => {},
  canEdit = false,
}) => {
  const { t } = useI18n();

  return (
    <div
      className={(canEdit) ? 'chatview-details-header clickable' : 'chatview-details-header'}
      onClick={(canEdit) ? onEditClick : undefined}
    >
      <div className="chatview-details-avatars">
        <RoomAvatar 
          avatarUrls={avatars}
          placeholderText={title || '?'}
        />
      </div>
      <div className="chatview-details-title-container">
        <span className="chatview-details-title">Name: {title}</span>
        {topic && <span className="chatview-details-topic">Topic: {topic}</span>}
                

        <span className="chatview-details-id">{t('ConversationDetailsView.conversationID') + `: ${id}`}</span>
        
      </div>
      {
        canEdit && (
          <div className="chatview-details-edit-icon">
            <ion-icon name="create-outline" />
          </div>
        )
      }
    </div>
  );
};

DetailsHeader.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  topic: PropTypes.string,
  avatars: PropTypes.arrayOf(PropTypes.string),
  onEditClick: PropTypes.func,
  canEdit: PropTypes.bool,
};

const LeaveBlock = ({
  onLeaveClick = () => {},
}) => {
  const { t } = useI18n();
  const { showConfirm } = useModal();
  const [hide, setHide] = useState(false);

  const handleLeaveClick = (e) => {
    e.stopPropagation();
    showConfirm(
      'Leave Room',
      'Are you sure you want to leave this room? This action cannot be undone and you will have to be reinvited.',
      onLeaveClick, // onConfirm
      null, // onCancel - just close modal
    );
  };

  return (
    <div className="chatview-details-block">
      <div
        className="chatview-details-block-header no-top-border"
        onClick={() => setHide(!hide)}
      >{t('ConversationDetailsView.leave')}</div>
      {
        !hide && (
          <div className="chatview-details-block-item" onClick={handleLeaveClick}>
            <div className="chatview-details-block-item-icon">
              <i className="fas fa-sign-out-alt" />
            </div>
            <span className="chatview-details-block-item-text">{t('ConversationDetailsView.leave')}</span>
          </div>
        )
      }
    </div>
  );
};

LeaveBlock.propTypes = {
  onLeaveClick: PropTypes.func,
};

const AdvancedSettingsBlock = ({
  onAdvancedSettingsClick = () => {},
  canConfigure = false,
}) => {
  const { t } = useI18n();
  const [hide, setHide] = useState(false);

  if (!canConfigure) {
    return null;
  }

  return (
    <div className="chatview-details-block">
      <div
        className="chatview-details-block-header no-top-border"
        onClick={() => setHide(!hide)}
      >
        {t('ConversationDetailsView.advancedSettings') || 'Advanced Settings'}
      </div>
      {
        !hide && (
          <div className="chatview-details-block-item" onClick={onAdvancedSettingsClick}>
            <div className="chatview-details-block-item-icon">
              <ion-icon name="settings-outline" />
            </div>
            <span className="chatview-details-block-item-text">
              {t('ConversationDetailsView.configureRoomSettings') || 'Configure Room Settings'}
            </span>
          </div>
        )
      }
    </div>
  );
};

AdvancedSettingsBlock.propTypes = {
  onAdvancedSettingsClick: PropTypes.func,
  canConfigure: PropTypes.bool,
};

const ParticipantsBlock = ({
  participants = [],
  onEditClick = () => {},
  onContactClick = () => {},
  canEdit = false,
}) => {
  const { t } = useI18n();
  const [hide, setHide] = useState(false);

  return (
    <div className="chatview-details-block">
      <div className="chatview-details-block-header" onClick={() => setHide(!hide)}>
        <SimpleTrans
          i18nKey="ConversationDetailsView.xPeopleInConversation"
          defaults="<0>{numPar}</0> {numPar, plural, =1 {Person} other {People}} in this conversation"
          components={[<span key="count" style={{ color: 'red', fontWeight: 'bold' }} />]}
          values={{ numPar: participants.length }}
        />
      </div>
      {
        !hide && canEdit && (
          <div className="chatview-details-block-item" onClick={onEditClick}>
            <div className="chatview-details-block-item-icon">
              <ion-icon name="add-outline" />
            </div>
            <span className="chatview-details-block-item-text">{t('ConversationDetailsView.editParticipants')}</span>
          </div>
        )
      }
      {
        !hide &&
        <ContactList
          contacts={participants}
          isLoading={false} 
          onSelectContact={(contactId) => onContactClick(contactId)}
        />
      }
    </div>
  );
};

ParticipantsBlock.propTypes = {
  participants: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  onEditClick: PropTypes.func,
  onContactClick: PropTypes.func,
  canEdit: PropTypes.bool,
}; 

const Details = ({
  id = null,
  title = '',
  topic = '',
  avatars = [],
  participants = [],
  onEditClick,
  onContactClick,
  onLeaveRoom,
  onAdvancedSettingsClick,
  canEdit = false,
  canConfigure = false,
  onClose = () => {},
}) => {
  const headerContent = (
    <DetailsHeader
      id={id}
      title={title}
      topic={topic}
      avatars={avatars}
      onEditClick={onEditClick}
      canEdit={canEdit}
    />
  );

  return (
    <RightSidebar
      isOpen={true}
      onClose={onClose}
      headerContent={headerContent}
      showCloseButton={false}
      className="chatview-details"
      closeOnClickOutside={false}
    >
      <LeaveBlock
        onLeaveClick={onLeaveRoom}
      />
      <AdvancedSettingsBlock
        onAdvancedSettingsClick={onAdvancedSettingsClick}
        canConfigure={canConfigure}
      />
      <ParticipantsBlock
        participants={participants}
        onEditClick={onEditClick}
        canEdit={canEdit} 
        onContactClick={(contactId) => onContactClick(contactId)}
      />
    </RightSidebar>
  );
};

Details.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  topic: PropTypes.string,
  avatars: PropTypes.arrayOf(PropTypes.string),
  participants: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  onEditClick: PropTypes.func.isRequired,
  onLeaveRoom: PropTypes.func.isRequired,
  onContactClick: PropTypes.func.isRequired,
  onAdvancedSettingsClick: PropTypes.func,
  canEdit: PropTypes.bool,
  canConfigure: PropTypes.bool,
  onClose: PropTypes.func,
};

export default Details;
